import {
  type PasswordResetRequest,
  v1UsersSendPasswordResetCreate,
} from '@on3/api/src';
import { type FormProps } from '@on3/ui-lib/components/Auth/Forms';
import { Form } from '@on3/ui-lib/components/Form';
import { Input } from '@on3/ui-lib/components/Input/Input';
import { Link } from '@on3/ui-lib/components/Link';
import { externalApi } from '@on3/ui-lib/index';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { object, string } from 'yup';

import styles from './AuthForm.module.scss';

const schema = object({
  email: string()
    .email('Email address is invalid')
    .required('Email is required'),
});

export const ForgotPasswordForm = ({
  showHeader = true,
  switchForm,
  callback,
}: FormProps) => {
  const { formatHref } = useDynamicRoute();
  const params = useSearchParams();
  const returnTo = params.get('returnTo');

  const [state, setState] = useState<PasswordResetRequest>({
    email: '',
  });
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (values: PasswordResetRequest) => {
    setError(null);

    try {
      await v1UsersSendPasswordResetCreate(externalApi, values);

      setSuccess(
        'Password reset email has been sent successfully. Please check your inbox and spam folder.',
      );
      callback?.();
    } catch (e: any) {
      setError('Password reset failed. Please check your email and try again.');
      console.error('Password reset error:', e);
    }
  };

  return (
    <Form
      className={styles.block}
      onSubmit={onSubmit}
      schema={schema}
      values={state}
    >
      {({ errors, processing }) => (
        <>
          {showHeader && (
            <header className={styles.header}>
              <h1>Restore Password</h1>
            </header>
          )}
          <div className={styles.fields}>
            <div className={styles.field}>
              <Input
                autoComplete="email"
                error={errors.email}
                id="email"
                label="Email Address"
                name="email"
                onChange={(e) => setState({ ...state, email: e.target.value })}
              />
            </div>
          </div>
          {error && <p className={styles.error}>{error}</p>}
          {success && <p className={styles.success}>{success}</p>}
          <button
            className={styles.btnSubmit}
            disabled={processing}
            type="submit"
          >
            Request Password Reset
          </button>
          <footer className={styles.footer}>
            {switchForm ? (
              <button onClick={() => switchForm('login')} type="button">
                Back to login
              </button>
            ) : (
              <Link
                href={formatHref({
                  path: '/[site]/login/',
                  params: { site: returnTo || '' },
                  options: {
                    resetQuery: true,
                  },
                })}
              >
                Back to login
              </Link>
            )}
          </footer>
        </>
      )}
    </Form>
  );
};
